import { Injectable } from '@angular/core';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { CasesService, OrdersQPS } from '../core/services/cases.service';
import { CasesState } from '../core/states/cases.state';
import { CaseDateFilters, MetaDataOrders } from './cases.facade';
import { Order } from '../shared/models/order';
import { Pagination } from '../shared/models/pagination';

interface CachedMonthOrders {
    data: Order[];
    pagination: Pagination;
    loadedAt: number;
}

/**
 * Caches the orders fetched for a case+month so navigating back and forth
 * between already visited months doesn't trigger a new GET /orders.
 * Ranges outside of 'month' mode (e.g. custom report ranges) are always
 * fetched fresh since they don't map to a single reusable cache key.
 */
@Injectable({
    providedIn: 'root',
})
export class OrdersTimelineService {
    private readonly CACHE_TTL_MS = 15 * 60 * 1000;
    private readonly cache = new Map<number, Map<string, CachedMonthOrders>>();
    private readonly inflight = new Map<string, Observable<Order[]>>();
    private readonly cacheVersions = new Map<number, number>();

    constructor(
        private casesService: CasesService,
        private casesState: CasesState,
    ) { }

    loadOrdersForRange(caseId: number, dateRange: CaseDateFilters): Observable<Order[]> {
        const qps: OrdersQPS = {
            page: 0,
            size: 1000,
            fromDate: dateRange?.historyFromDate,
            toDate: dateRange?.historyToDate,
            filterByProvisionValidity: true,
        };

        const monthKey = this.getMonthKey(dateRange);
        if (monthKey == null) {
            return this.fetch(caseId, null, qps);
        }

        const caseCache = this.cache.get(caseId);
        const cached = caseCache?.get(monthKey);
        if (cached && Date.now() - cached.loadedAt < this.CACHE_TTL_MS) {
            this.publish(qps, cached.data, cached.pagination);
            return of(cached.data);
        }

        return this.fetch(caseId, monthKey, qps);
    }

    invalidateCase(caseId: number | null): void {
        if (caseId == null) {
            return;
        }

        this.cache.delete(caseId);
        this.cacheVersions.set(caseId, (this.cacheVersions.get(caseId) ?? 0) + 1);
        Array.from(this.inflight.keys())
            .filter(key => key.startsWith(`${caseId}|`))
            .forEach(key => this.inflight.delete(key));
    }

    cleanStore(): void {
        this.cache.clear();
        this.inflight.clear();
        this.cacheVersions.clear();
    }

    private getMonthKey(dateRange: CaseDateFilters): string | null {
        if (dateRange?.mode !== 'month' || !dateRange.historyFromDate) {
            return null;
        }

        return moment(dateRange.historyFromDate).format('YYYY-MM');
    }

    private fetch(caseId: number, monthKey: string | null, qps: OrdersQPS): Observable<Order[]> {
        const key = `${caseId}|${monthKey ?? `${qps.fromDate?.getTime()}-${qps.toDate?.getTime()}`}`;
        const version = this.cacheVersions.get(caseId) ?? 0;
        const existing = this.inflight.get(key);
        if (existing) {
            return existing;
        }

        // Avoid showing stale orders from a previous case/month while this request is in flight.
        this.casesState.setOrders([]);
        this.casesState.setOrdersToDisplayOnScreen([]);
        this.casesState.setLoadingGettingOrders(true);

        const request$ = this.casesService.getOrders(caseId, qps).pipe(
            tap(body => {
                if ((this.cacheVersions.get(caseId) ?? 0) !== version) {
                    return;
                }
                if (monthKey != null) {
                    const targetCache = this.cache.get(caseId) ?? new Map<string, CachedMonthOrders>();
                    targetCache.set(monthKey, { data: body.data, pagination: body.pagination, loadedAt: Date.now() });
                    this.cache.set(caseId, targetCache);
                }
                this.publish(qps, body.data, body.pagination);
            }),
            finalize(() => {
                if (this.inflight.get(key) === request$) {
                    this.casesState.setLoadingGettingOrders(false);
                    this.casesState.setLoadingGettingPagination(false);
                    this.inflight.delete(key);
                }
            }),
            map(body => body.data),
            shareReplay(1),
        );
        this.inflight.set(key, request$);
        return request$;
    }

    private publish(qps: OrdersQPS, data: Order[], pagination: Pagination): void {
        this.casesState.setOrders(data);
        this.casesState.setMetaDataOrders({ ...qps, ...pagination } as MetaDataOrders);
    }
}
